/**
 * Fleet vendor ceilings (plan `fleet-app-delivery` U2, KTD3/KTD6).
 *
 * Four per-N ceilings will bind between the current ~14 apps and 1,000, each
 * owned by a different vendor and none of them measured until now. One — the EAS
 * project cap — has a decision attached that **cannot be retrofitted after app
 * 100**, because `updates.url` is baked into every shipped binary.
 *
 * Every row carries an `authority`: the named source the number came from. Per
 * KTD6, a count without a named source cannot be distinguished from a stale
 * guess, and a check that does not expose a scope number cannot be distinguished
 * from a no-op.
 *
 * The `state` vocabulary is deliberately the EXISTING readiness one
 * (`present` / `not-applicable` / `unreadable`) rather than a new set of words:
 *
 *  - `present`        — the count was measured and the limit is known, so
 *                       headroom is real.
 *  - `not-applicable` — the vendor imposes no limit at all. Apple charges per
 *                       developer account but caps nothing, so a headroom number
 *                       would be fabricated. The current count is still reported,
 *                       because it doubles as migration progress toward one team
 *                       per school (KD9).
 *  - `unreadable`     — the authority exists but holds nothing to count, so no
 *                       number can be established. `google_project_id` is unset
 *                       on every registry row today, which makes the Firebase
 *                       grouping legitimately empty — reporting that as `0 apps
 *                       used / 30` would be false headroom on the exact axis that
 *                       forces a project-pooling decision. `eas-projects` uses
 *                       it for the same reason whenever Expo — the authority
 *                       that enforces that cap — cannot be asked: our own
 *                       registry count is NOT a stand-in for it (on 2026-08-18
 *                       the registry said 14 and Expo said 17).
 *
 * `absent` is the fourth word in that vocabulary and does not occur here: a
 * ceiling either has a limit, has none by design, or cannot be read.
 */

import { z } from 'zod'

/** Closed set, so callers can branch without string matching. */
export const FLEET_CEILING_KINDS = [
  'eas-projects',
  'apple-teams',
  'firebase-apps-per-project',
  'github-matrix-jobs'
] as const

export const FleetCeilingKindSchema = z.enum(FLEET_CEILING_KINDS)
export type FleetCeilingKind = z.infer<typeof FleetCeilingKindSchema>

export const FleetCeilingStateSchema = z.enum(['present', 'not-applicable', 'unreadable'])
export type FleetCeilingState = z.infer<typeof FleetCeilingStateSchema>

const FleetCeilingFieldsSchema = z.object({
  kind: FleetCeilingKindSchema,
  /** Measured count, or `null` when it cannot be established. */
  current: z.number().int().nonnegative().nullable(),
  /** Vendor limit, `null` when the vendor imposes none or it is unknown. */
  limit: z.number().int().positive().nullable(),
  /** `limit - current`, `null` whenever either side is unknown or absent. */
  headroom: z.number().int().nullable(),
  state: FleetCeilingStateSchema,
  /** The named source of `current` and `limit`. Never empty — see KTD6. */
  authority: z.string().min(1),
  /**
   * Why this row is not `present`. Required for every non-`present` state so an
   * unmeasured ceiling can never render as a clean pass.
   */
  reason: z.string().min(1).nullable()
})

/**
 * The invariant lives in the contract, not only in the producer: a ceiling that
 * is not `present` MUST say why, and a `present` one must carry real numbers.
 * Without this, "unmeasured" degrades into an empty row that reads like a clean
 * pass — the precise failure KTD6 exists to prevent, and one this codebase has
 * already shipped three times under other names.
 */
export const FleetCeilingSchema = FleetCeilingFieldsSchema.superRefine((ceiling, ctx) => {
  if (ceiling.state !== 'present' && ceiling.reason === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reason'],
      message: `a ceiling in state "${ceiling.state}" must state a reason — an unmeasured ceiling must not render as a clean row`
    })
  }
  if (ceiling.state === 'present' && (ceiling.current === null || ceiling.limit === null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['state'],
      message: 'state "present" claims a real measurement, so current and limit must both be known'
    })
  }
})
export type FleetCeiling = z.infer<typeof FleetCeilingSchema>

/**
 * Where our registry and Expo's account disagree about which projects exist.
 *
 * The `eas-projects` ceiling counts what EXPO reports, because Expo's cap is
 * what binds. That leaves a second, separately useful fact: a project living in
 * EAS with no `MobileApp` row is invisible to every registry-driven surface we
 * have, and is precisely how the fleet drifts out from under its own inventory
 * (3 such projects on 2026-08-18: `alcuin-sj-mobile`,
 * `north-shore-country-day-sj-mobile`, `sacred-heart-academy`).
 *
 * `null` whenever the Expo side is unreadable — a divergence cannot be computed
 * against a number that was never obtained, and reporting "0 divergences" there
 * would be the same false-clean-row failure `unreadable` exists to prevent.
 */
export const EasProjectDivergenceSchema = z.object({
  /** Projects Expo reports for the account. */
  expoProjectCount: z.number().int().nonnegative(),
  /** Registry rows holding a usable (non-placeholder) `expo_project_id`. */
  registryProjectCount: z.number().int().nonnegative(),
  /** Slugs of projects in EAS with no registry row — the drift direction that matters. */
  inExpoNotInRegistry: z.array(z.string()),
  /** `expo_project_id` values the registry holds that Expo does not list for this account. */
  inRegistryNotInExpo: z.array(z.string())
})
export type EasProjectDivergence = z.infer<typeof EasProjectDivergenceSchema>

export const FleetCeilingsResponseSchema = z.object({
  ceilings: z.array(FleetCeilingSchema),
  /**
   * Additive (optional) so a consumer pinned to the pre-Expo payload keeps
   * parsing. `null` means the Expo side could not be read this run.
   */
  easProjectDivergence: EasProjectDivergenceSchema.nullable().optional(),
  /**
   * The scope denominator: how many registry rows every count was taken over.
   * Per KTD6 this is not decoration — without it, "8 Apple teams" cannot be
   * distinguished from "8 Apple teams out of 14 apps we could see".
   */
  registryRowsScanned: z.number().int().nonnegative(),
  generatedAt: z.string().datetime()
})
export type FleetCeilingsResponse = z.infer<typeof FleetCeilingsResponseSchema>
